// DataExtractor.ts
import { GET_ACTIONS } from 'data/actions';
import { ExportFormat, ExportTimeRangeOptions, ExportTypes, ScoreBoardExportSections } from 'types/export';
import { Client, gql } from 'urql';
import { getLast30Days, getLast7Days, getLastYear } from './timePeriods';
import { Action } from 'data/actions/types';
import { GET_OBJECTIVES } from 'data/objectives';
import { Objective } from 'data/objectives/types';
import { GET_MEASURABLES, getConditional } from 'data/measurables';
import { getValueString } from 'pages/company/[company]/team/[team]/scoreboard/[measurable]';

interface CustomTimeRange {
  startDate: Date;
  endDate: Date;
}

// Utility function to format goal values for export
export function formatGoalForExport(measurable: {
  conditional?: string;
  goal?: number | string;
  type?: string;
  min_range?: number | string;
  max_range?: number | string;
}): string {
  const { conditional, goal, type, min_range, max_range } = measurable;

  if (conditional === 'range') {
    const min = getValueString(min_range, type);
    const max = getValueString(max_range, type);
    return min && max ? `${min} - ${max}` : '';
  } else if (conditional) {
    const conditionalSymbol = getConditional(conditional as any)?.label || '';
    const goalValue = getValueString(goal, type);
    return conditionalSymbol ? `${conditionalSymbol} ${goalValue}` : goalValue;
  } else {
    return getValueString(goal, type);
  }
}
const extractTextFromDescription = (input) => {
  try {
    // Handle null/undefined
    if (input == null) return null;

    // If input is a string, try parsing
    const parsed = typeof input === 'string' ? JSON.parse(input) : input;

    // Validate blocks
    if (!parsed.blocks || !Array.isArray(parsed.blocks)) return null;

    // Collect text values
    const texts = parsed.blocks
      .map((block) => (block && typeof block.text === 'string' ? block.text : null))
      .filter(Boolean);

    // Return single string if only one, otherwise array
    return texts.length === 1 ? texts[0] : '';
  } catch (err) {
    console.error('Failed to extract text:', err.message);
    return null;
  }
};

class DataExtractor {
  private client: Client;
  private static instance: DataExtractor;

  // Make the constructor private to prevent direct construction calls with the `new` operator.
  private constructor(client: Client) {
    this.client = client;
  }

  // Provides the global point of access to the instance and ensures only one instance is created.
  public static getInstance(client: Client): DataExtractor {
    if (!DataExtractor.instance) {
      DataExtractor.instance = new DataExtractor(client);
    }
    return DataExtractor.instance;
  }

  async extractData(
    timeRange: ExportTimeRangeOptions,
    exportType: ExportTypes,
    teamId: number,
    companyId: number,
    customTimeRange?: CustomTimeRange,
  ) {
    switch (exportType) {
      case ExportTypes.Actions:
        return this.getActions(timeRange, teamId, companyId, customTimeRange);
      case ExportTypes.Objectives:
        return this.getObjectives(timeRange, teamId, companyId, customTimeRange);
      case ExportTypes.Scoreboard:
        return this.getScoreboardData(timeRange, teamId, companyId, customTimeRange);
      default:
        throw new Error('Unsupported data type');
    }
  }

  public async extractDataForMultipleSections(
    comapanyId: number,
    teamId: number,
    exportType: ExportTypes,
    timeRange: ExportTimeRangeOptions,
    customTimeRange?: CustomTimeRange,
    sections?: ScoreBoardExportSections[],
  ) {
    switch (exportType) {
      case ExportTypes.Scoreboard:
        return this.extractMultipleScoreboardSections(comapanyId, teamId, timeRange, customTimeRange, sections);
      default:
        throw new Error('Unsupported export type for sections extraction.');
    }
  }

  private async extractMultipleScoreboardSections(
    companyId: number,
    teamId: number,
    timeRange: ExportTimeRangeOptions,
    customTimeRange?: CustomTimeRange,
    sections?: ScoreBoardExportSections[],
  ) {
    // For scoreboard, we want all measurables regardless of time range
    // The time range will be used to filter scores if needed
    const where: Record<string, unknown> = {
      company_id: { _eq: companyId },
      teams_id: { _eq: teamId },
      archived: { _neq: true },
    };

    // Filter by selected sections if specified
    if (sections && sections.length > 0) {
      // Convert enum values to lowercase to match database values
      const lowercaseSections = sections.map((section) => section.toLowerCase());
      where.interval = { _in: lowercaseSections };
    }

    // If we have a custom time range, we can use it to filter scores
    let scoreDateFilter = null;
    if (customTimeRange && timeRange === ExportTimeRangeOptions.Custom) {
      scoreDateFilter = {
        date: {
          _gte: customTimeRange.startDate.toISOString(),
          _lte: customTimeRange.endDate.toISOString(),
        },
      };
    }

    const results = await this.client
      .query(GET_MEASURABLES, {
        where,
        scoreDateFilter,
      })
      .toPromise();

    const measurablesData = results?.data?.measurables || [];

    // Transform the data into the same structure as CSV generation expects
    const measurableMap = new Map();

    measurablesData.forEach((measurable) => {
      // Initialize measurable row
      measurableMap.set(measurable.title, {
        interval: measurable.interval,
        title: measurable.title,
        description: extractTextFromDescription(measurable.description),
        goal: formatGoalForExport(measurable), // Using the utility function to format goal
        owner: measurable.user?.name || '',
        trend: measurable.trend || '',
        min_range: measurable.min_range || '',
        max_range: measurable.max_range || '',
        scores: {}, // Will store date -> score mapping
      });

      // Add score rows if they exist
      if (measurable.scores && Array.isArray(measurable.scores)) {
        measurable.scores.forEach((score) => {
          const measurableData = measurableMap.get(measurable.title);
          if (measurableData && score.date) {
            measurableData.scores[score.date] = score.value ?? '';
          }
        });
      }
    });

    const exportData = Array.from(measurableMap.values());
    return exportData;
  }

  private async getActions(
    timeRange: ExportTimeRangeOptions,
    teamId: number,
    companyId: number,
    customTimeRange?: CustomTimeRange,
  ) {
    // Logic to determine the 'where' condition based on timeRange
    const timeRanges = this.obtainTimeRanges(timeRange, 'date', customTimeRange);
    const where = { ...timeRanges, company_id: { _eq: companyId }, teams_id: { _eq: teamId } };
    const result = await this.client.query(GET_ACTIONS, { where }).toPromise();
    const cleanedActions = await this.cleanActionData(result?.data?.actions || []);
    return cleanedActions;
  }

  private async getObjectives(
    timeRange: ExportTimeRangeOptions,
    teamId: number,
    companyId: number,
    customTimeRange?: CustomTimeRange,
  ) {
    // Similar to getActions, implement querying for objectives
    const timeRanges = this.obtainTimeRanges(timeRange, 'created_at', customTimeRange);
    const where = { ...timeRanges, company_id: { _eq: companyId }, teams_id: { _eq: teamId } };
    const result = await this.client.query(GET_OBJECTIVES, { where }).toPromise();
    const cleanedObjectives = this.cleanObjectivesData(result?.data?.objectives) || [];
    return cleanedObjectives;
  }

  private async getScoreboardData(
    timeRange: ExportTimeRangeOptions,
    teamId: number,
    companyId: number,
    customTimeRange?: CustomTimeRange,
  ) {
    // For scoreboard, we want all measurables regardless of time range
    // The time range will be used to filter scores if needed
    const where = {
      company_id: { _eq: companyId },
      teams_id: { _eq: teamId },
      archived: { _neq: true },
    };

    // If we have a custom time range, we can use it to filter scores
    let scoreDateFilter = null;
    if (customTimeRange && timeRange === ExportTimeRangeOptions.Custom) {
      scoreDateFilter = {
        date: {
          _gte: customTimeRange.startDate.toISOString(),
          _lte: customTimeRange.endDate.toISOString(),
        },
      };
    }

    const results = await this.client
      .query(GET_MEASURABLES, {
        where,
        scoreDateFilter,
      })
      .toPromise();
    const measurablesData = results?.data?.measurables || [];

    // Transform the data into the same structure as CSV generation
    const measurableMap = new Map();

    measurablesData.forEach((measurable) => {
      // Initialize measurable row
      measurableMap.set(measurable.title, {
        interval: measurable.interval,
        title: measurable.title,
        description: extractTextFromDescription(measurable.description),
        goal: String(formatGoalForExport(measurable)),
        owner: measurable.user?.name || '',
        trend: measurable.trend || '',
        min_range: measurable.min_range || '',
        max_range: measurable.max_range || '',
        scores: {}, // Will store date -> score mapping
      });

      // Add score rows if they exist
      if (measurable.scores && Array.isArray(measurable.scores)) {
        measurable.scores.forEach((score) => {
          const measurableData = measurableMap.get(measurable.title);
          if (measurableData && score.date) {
            measurableData.scores[score.date] = score.value ?? '';
          }
        });
      }
    });

    const result = Array.from(measurableMap.values());
    return result;
  }

  private obtainTimeRanges = (
    timeRange: ExportTimeRangeOptions,
    propertyValue: string,
    customDates?: CustomTimeRange,
  ) => {
    let clause;
    switch (timeRange) {
      case ExportTimeRangeOptions.LastSevenDays:
        // const { startDate: start7, endDate: end7 } = getLast7Days();
        // clause = { [propertyValue]: { _gte: start7.toISOString(), _lte: end7.toISOString() } };
        return clause;
      case ExportTimeRangeOptions.LastThirtyDays:
        // const { startDate: start30, endDate: end30 } = getLast30Days();
        // clause = { [propertyValue]: { _gte: start30.toISOString(), _lte: end30.toISOString() } };
        return clause;
      case ExportTimeRangeOptions.LastYear:
        // const { startDate: startY, endDate: endY } = getLastYear();
        // clause = { [propertyValue]: { _gte: startY.toISOString(), _lte: endY.toISOString() } };
        return clause;
      case ExportTimeRangeOptions.All:
        clause = {};
        return clause;
      case ExportTimeRangeOptions.Custom:
        clause = {};
        clause = {
          [propertyValue]: { _gte: customDates.startDate.toISOString(), _lte: customDates.endDate.toISOString() },
        };
        return clause;
      default:
        throw new Error('Unsupported time range');
    }
  };

  private cleanActionData(actions: Action[]) {
    return actions.map((action) => {
      try {
        // Assuming the description is a JSON string that needs parsing
        const descriptionObj = JSON.parse(action.description || '{}');
        const blocks = descriptionObj.blocks || [];
        const firstBlock = blocks[0] || {};
        const text = firstBlock.text || '';
        let attachmenthsString = '';

        if (action.action_files && action.action_files.length > 0) {
          action.action_files.forEach((a, index) => {
            if (index >= 1) {
              attachmenthsString += ` | ${a.url}`;
            } else {
              attachmenthsString += a.url;
            }
          });
        }

        let commentString = '';
        if (Array.isArray(action.action_comments)) {
          action.action_comments.map((com) => {
            commentString += this.extractTextFromAttribute(com, 'comment');
            commentString += ` ${new Date(com.timestamp).toLocaleString()} |`;
          });
        }

        return {
          ...action,
          description: text, // Update the description with the extracted text
          attachments: attachmenthsString,
          assignee: action.user?.name,
          action_comments: commentString,
        };
      } catch (error) {
        console.error('Error cleaning action:', error);
        return action; // Return the action unchanged in case of parsing error
      }
    });
  }

  private cleanObjectivesData(objectives: Objective[]): Objective[] {
    return objectives?.map((objective) => {
      try {
        return {
          ...objective,
          description: this.extractTextFromAttribute(objective, 'description'),
          milestone30days: this.extractTextFromAttribute(objective, 'column_30_day'),
          milestone60days: this.extractTextFromAttribute(objective, 'column_60_day'),
          keyResults: this.extractTextFromAttribute(objective, 'key_results'),
          owned_user: objective?.user?.name,
        };
      } catch (error) {
        console.error('Error cleaning action:', error);
        return objective; // Return the action unchanged in case of parsing error
      }
    });
  }

  public generateScoreboardCSV(data: any[]): string {
    if (!data || data.length === 0) {
      console.log('No data provided to generateScoreboardCSV');
      return '';
    }

    // Get all unique dates across all measurables
    const allDates = new Set<string>();
    data.forEach((measurable) => {
      if (measurable.scores && typeof measurable.scores === 'object') {
        Object.keys(measurable.scores).forEach((date) => {
          allDates.add(date);
        });
      }
    });

    // Sort dates chronologically
    const sortedDates = Array.from(allDates).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    // Define CSV headers
    const headers = [
      'Interval',
      'Title',
      'Description',
      'Goal',
      'Owner',
      'Trend',
      // 'Min Range',
      // 'Max Range',
      ...sortedDates.map((date) => `Score ${date}`),
    ];

    // Convert data to CSV rows
    const csvRows = [headers.join(',')];

    data.forEach((measurable) => {
      const values = [
        measurable.interval || '',
        `"${(measurable.title || '').replace(/"/g, '""')}"`,
        `"${(measurable.description || '').replace(/"/g, '""')}"`,
        `"${(measurable.goal || '').replace(/"/g, '""')}"`,
        `"${(measurable.owner || '').replace(/"/g, '""')}"`,
        measurable.trend || '',
        // measurable.min_range || '',
        // measurable.max_range || '',
        ...sortedDates.map((date) => (measurable.scores ? measurable.scores[date] ?? '' : '')),
      ];
      csvRows.push(values.join(','));
    });

    const csvContent = csvRows.join('\n');

    // If no scores were found, add a note
    if (sortedDates.length === 0) {
      console.log('Warning: No score dates found in the data');
    }

    return csvContent;
  }

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  private extractTextFromAttribute(objective: { [key: string]: any }, attributeName: string): string {
    try {
      // Parse the JSON string from the specified attribute
      const attributeObj = JSON.parse(objective[attributeName] || '{}');
      const blocks = attributeObj.blocks || [];
      const firstBlock = blocks[0] || {};
      const text = firstBlock.text || '';
      return text;
    } catch (error) {
      console.error(`Error extracting text from attribute ${attributeName}:`, error);
      return ''; // Return empty string in case of parsing error
    }
  }
}

export default DataExtractor;
